<template>
  <div v-if="configurableTableColumnsEnabled" class="column-configurator">
    <v-btn
      v-if="!hideActivator"
      icon
      small
      class="column-configurator-activator"
      :aria-label="$t('configure_columns')"
      @click.stop="openDialog">
      <v-icon small>mdi-cog</v-icon>
    </v-btn>
    <v-dialog width="500" v-model="dialog">
      <v-card>
        <v-card-title class="column-configurator-title">
          {{ $t('configure_columns') }}
          <v-chip
            x-small
            color="secondary"
            text-color="primary-900"
            class="column-configurator-beta-chip ml-2 font-weight-bold text-uppercase">
            {{ $t('beta') }}
          </v-chip>
          <v-tooltip bottom max-width="320">
            <template #activator="{ on, attrs }">
              <v-icon small class="ml-2" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <span>{{ $t('column_configurator_help') }}</span>
          </v-tooltip>
        </v-card-title>
        <v-card-text class="column-configurator-list">
          <!-- Beta label (VF-4453), with the chip in the title. Brand primary on
               purpose: this theme's "info" color is green, so no type="info". -->
          <v-alert
            dense
            text
            border="left"
            color="primary"
            icon="mdi-flask-outline"
            class="column-configurator-beta-banner text-body-2">
            <strong>{{ $t('column_configurator_beta_banner_title') }}</strong> <i18n path="column_configurator_beta_banner" tag="span">
              <template #link>
                <a :href="supportRequestUrl" target="_blank" rel="noopener" class="text-decoration-underline">{{ $t('column_configurator_beta_banner_link') }}</a>
              </template>
            </i18n>
          </v-alert>
          <div class="column-configurator-drag-instructions text-caption mb-2">
            {{ $t('column_configurator_drag_instructions') }}
          </div>
          <div class="column-configurator-headers d-flex align-center py-1 font-weight-bold">
            <v-icon small class="mr-2" style="visibility: hidden">mdi-drag-horizontal-variant</v-icon>
            <span class="flex-grow-1">{{ $t('column') }}</span>
            <span class="column-configurator-control text-center">{{ $t('show_hide') }}</span>
          </div>
          <!-- Primary column: pinned at position 0, structurally outside the
               draggable list so it can never be moved or displaced. -->
          <div
            v-if="primaryColumn"
            class="column-configurator-primary-row column-configurator-row d-flex align-center py-1">
            <v-icon small class="mr-2" style="visibility: hidden">mdi-drag-horizontal-variant</v-icon>
            <span class="flex-grow-1">{{ primaryColumn.label }}</span>
            <span class="column-configurator-control">
              <v-tooltip bottom max-width="320">
                <template #activator="{ on, attrs }">
                  <v-icon small v-bind="attrs" v-on="on">mdi-lock</v-icon>
                </template>
                <span>{{ $t('column_configurator_locked_column_tooltip') }}</span>
              </v-tooltip>
            </span>
          </div>
          <draggable
            v-model="reorderableColumns"
            handle=".column-configurator-drag-handle"
            :animation="150">
            <div
              v-for="column in reorderableColumns"
              :key="column.id"
              class="column-configurator-row d-flex align-center py-1">
              <v-icon small class="column-configurator-drag-handle mr-2">mdi-drag-horizontal-variant</v-icon>
              <span class="flex-grow-1">{{ column.label }}</span>
              <span class="column-configurator-control">
                <v-icon v-if="column.hideable === false" small>mdi-lock</v-icon>
                <v-switch
                  v-else
                  dense
                  hide-details
                  class="mt-0 pt-0"
                  :input-value="column.visible"
                  @change="toggleColumn(column.id, $event)" />
              </span>
            </div>
          </draggable>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            class="column-configurator-reset"
            :disabled="isAtDefaults"
            @click="resetToDefaults">
            {{ $t('show_all_and_reset_order') }}
          </v-btn>
          <v-spacer />
          <v-btn text @click="dialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { cloneDeep, isEqual } from 'lodash'
import draggable from 'vuedraggable'

/**
 * Synthetic v-data-table header adopting tables append as their LAST header
 * (only when the feature flag getter is true) so the gear icon renders inside
 * the table with no data cells (VF-4197).
 */
export const COLUMN_CONFIGURATOR_HEADER = {
  text: '',
  value: '__columnConfigurator',
  sortable: false,
  align: 'end',
  width: 48
}

/**
 * Path of the Support Center request form linked from the Beta banner. Kept
 * here, not in the translation catalog, so translated strings never carry the
 * URL. The domain comes from VUE_APP_ZENDESK_DOMAIN, like every other Support link.
 */
export const SUPPORT_REQUEST_PATH = '/hc/en-us/requests/new?ticket_form_id=35359702491671'

/**
 * Reusable column configurator modal (VF-4174, epic VF-4136).
 *
 * Table-agnostic: receives the current ordered column registry and the
 * table's defaults via props, emits the full new registry on every change.
 * Persistence is NOT handled here (VF-4175 listens to the events).
 *
 * Column definition: { id, label, visible, primary, hideable, reorderable? }
 *   - id MUST equal the Vuetify header `value` (product columns: product_{id}, VF-4177).
 *   - primary: exactly one; fixed at position 0, cannot be dragged or hidden.
 *   - hideable false: reorderable but always visible.
 *   - reorderable: optional; derived as !primary and normalized on every emit.
 *     Unknown extra fields on column objects are preserved verbatim.
 *
 * Events:
 *   - update:columns (columns, tableId) — new deep-cloned ordered registry; apply immediately.
 *   - reset (tableId) — clear saved preferences for this table. No
 *     update:columns follows: the owner resolves defaults itself once the
 *     saved entry is gone, so defaults are never re-persisted.
 *
 * Renders nothing when featureModule/configurableTableColumnsEnabled is false (VF-4173).
 */
export default {
  name: 'ColumnConfigurator',
  components: {
    draggable
  },
  props: {
    tableId: {
      type: String,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    defaultColumns: {
      type: Array,
      required: true
    },
    /**
     * Suppress the built-in gear button so the owner can place its own
     * activator elsewhere and call `openDialog()` on this component (ref).
     *
     * Needed by tables that show the gear inside a v-data-table header
     * (VF-4197): Vuetify's VDataTableHeaderDesktop renders its `<th>` vnodes
     * WITHOUT keys, so hiding/showing a column shortens the header row and
     * Vue re-patches the trailing `<th>`s by position. A component sitting in
     * the last `<th>` is therefore destroyed and re-created on every toggle,
     * taking the open dialog (and its local state) with it. Keeping this
     * component mounted OUTSIDE the table and leaving only a stateless button
     * in the header avoids the remount entirely.
     */
    hideActivator: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dialog: false,
      localColumns: []
    }
  },
  computed: {
    zendeskDomain () {
      return process.env.VUE_APP_ZENDESK_DOMAIN
    },
    supportRequestUrl () {
      return `https://${this.zendeskDomain}${SUPPORT_REQUEST_PATH}`
    },
    ...mapGetters('featureModule', ['configurableTableColumnsEnabled']),
    /**
     * True when the modal's current state already matches the table defaults
     * (same column order, same visibility) — there is nothing to reset, so
     * the reset button is disabled. Compares only id order and effective
     * visibility: other fields (labels, extra metadata) are irrelevant to
     * what reset would change.
     * @return Boolean
     */
    isAtDefaults () {
      const shape = columns => columns.map(c => ({ id: c.id, visible: c.visible !== false }))
      return isEqual(shape(this.localColumns), shape(this.defaultColumns))
    },
    /**
     * The single primary column (fixed position 0). Null-safe for
     * misconfigured registries.
     * @return Object|null
     */
    primaryColumn () {
      return this.localColumns.find(c => c.primary === true) || null
    },
    /**
     * All non-primary columns in display order. The setter is invoked by
     * vuedraggable on drop: reassemble with the primary pinned first and emit.
     */
    reorderableColumns: {
      get () {
        return this.localColumns.filter(c => c.primary !== true)
      },
      set (newOrder) {
        const primary = this.primaryColumn
        this.localColumns = primary ? [primary, ...newOrder] : [...newOrder]
        this.emitColumns()
      }
    }
  },
  methods: {
    /**
     * Clone the current registry into local state and show the modal.
     * Guarded on the flag (VF-4192): even a programmatic call cannot open
     * the modal when the feature is off.
     */
    openDialog () {
      if (!this.configurableTableColumnsEnabled) {
        return
      }
      this.localColumns = cloneDeep(this.columns)
      this.dialog = true
    },
    /**
     * Toggle a configurable column's visibility. Ignored for primary and
     * non-hideable columns — the matrix is enforced here regardless of what
     * the template (or a stale saved config) tries to do.
     * @param {String} columnId
     * @param {Boolean} visible
     */
    toggleColumn (columnId, visible) {
      const column = this.localColumns.find(c => c.id === columnId)
      if (!column || column.primary === true || column.hideable === false) {
        return
      }
      column.visible = Boolean(visible)
      this.emitColumns()
    },
    /**
     * Emit the full ordered registry (deep-cloned, primary/non-hideable
     * visibility forced true, reorderable normalized to !primary) so the
     * owning table and the persistence layer (VF-4175) can react
     * immediately. Extra fields on column objects (e.g. route_product_id)
     * survive the clone untouched.
     */
    emitColumns () {
      const columns = cloneDeep(this.localColumns)
      columns.forEach(c => {
        if (c.primary === true || c.hideable === false) {
          c.visible = true
        }
        c.reorderable = c.primary !== true
      })
      this.$emit('update:columns', columns, this.tableId)
    },
    /**
     * Restore the table's built-in defaults (order + visibility) and tell the
     * persistence layer to CLEAR this table's saved entry (VF-4198) — clearing
     * rather than overwriting lets future default changes flow through.
     * Deliberately NO update:columns emission here: re-emitting the defaults
     * would let the persistence layer save them as a user preference,
     * defeating the clear. The owner recomputes the registry once the saved
     * entry is gone and the `columns` watcher syncs the open modal.
     * Only this table is affected; the modal stays open.
     */
    resetToDefaults () {
      this.localColumns = cloneDeep(this.defaultColumns)
      this.$emit('reset', this.tableId)
    }
  },
  watch: {
    // Keep local state in sync if the owner recomputes the registry while the
    // modal is open (e.g. persistence hydration or a WebSocket sync, VF-4175).
    columns (newColumns) {
      if (this.dialog) {
        this.localColumns = cloneDeep(newColumns)
      }
    }
  }
}
</script>

<style scoped>
.column-configurator {
  display: inline-block;
}

/* VF-4195 AC: the column list is scrollable — long registries (e.g. many
   dynamic product columns) must not push the footer off-screen. */
.column-configurator-list {
  max-height: 60vh;
  overflow-y: auto;
}

/* Right-hand cell shared by the "Show/Hide" header, the lock icons and the
   visibility switches: one fixed width with centered content so they all
   line up in the same column regardless of the control rendered inside. */
.column-configurator-control {
  flex: 0 0 88px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Vuetify gives the switch input an 8px right margin (room for a label we
   don't render) — drop it so the switch is truly centered in the cell. */
.column-configurator-control ::v-deep .v-input--selection-controls__input {
  margin-right: 0;
}
</style>
